
import type { DentalOfficeProvider } from '../../types';

interface ProviderListProps {
  providers: DentalOfficeProvider[];
  onEdit: (provider: DentalOfficeProvider) => void;
  onDelete: (provider: DentalOfficeProvider) => void;
  canManage: boolean;
}

const roleLabels: Record<string, string> = {
  dentist: 'Tannlæknir',
  hygienist: 'Tannfræðingur',
  admin: 'Stjórnandi',
  receptionist: 'Ritari',
  assistant: 'Aðstoðarmaður',
};

export function ProviderList({ providers, onEdit, onDelete, canManage }: ProviderListProps) {
  const formatPermissions = (provider: DentalOfficeProvider): string => {
    const perms: string[] = [];
    if (provider.can_view_records) perms.push('Skoða');
    if (provider.can_edit_records) perms.push('Breyta');
    if (provider.can_schedule) perms.push('Tímabóka');
    if (provider.can_billing) perms.push('Greiðslur');
    return perms.join(', ') || 'Engar heimildir';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Nafn
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Netfang
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Hlutverk
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Heimildir
            </th>
            {canManage && (
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Aðgerðir
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {providers.map((provider) => (
            <tr key={provider.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-semibold text-sm">
                      {provider.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">{provider.name}</div>
                    {!provider.is_active && (
                      <div className="text-xs text-red-600">Óvirkur</div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{provider.email}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                  {roleLabels[provider.role] || provider.role}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-600">{formatPermissions(provider)}</div>
              </td>
              {canManage && (
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => onEdit(provider)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                  >
                    Breyta
                  </button>
                  <button
                    onClick={() => onDelete(provider)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Eyða
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {providers.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Engir starfsmenn skráðir
        </div>
      )}
    </div>
  );
}
